import { PUBLIC_POCKETBASE_URL } from '$app/env/public';

type BodyMethod = 'POST' | 'PUT' | 'PATCH';
type NoBodyMethod = 'GET' | 'DELETE' | 'HEAD' | 'OPTIONS';
type HttpMethod = BodyMethod | NoBodyMethod;

type ApiFetchOptions = Omit<RequestInit, 'method' | 'body'> & {
	dontThrowOnBadStatus?: boolean;
};

export abstract class BaseApiService {
	protected readonly baseURL: string;

	constructor(
		baseURL = PUBLIC_POCKETBASE_URL,
		private readonly tokenOrApiKey?: string
	) {
		this.baseURL = baseURL;
	}

	private get authInit(): Pick<RequestInit, 'headers' | 'credentials'> {
		if (this.tokenOrApiKey) {
			return { headers: { Authorization: `Bearer ${this.tokenOrApiKey}` } };
		}
		return { credentials: 'include' };
	}

	private async _request(
		method: HttpMethod,
		path: string,
		bodyOrOptions?: unknown,
		options?: ApiFetchOptions
	): Promise<Response> {
		const isNoBody =
			method === 'GET' || method === 'DELETE' || method === 'HEAD' || method === 'OPTIONS';
		const body = isNoBody ? undefined : bodyOrOptions;
		const {
			headers: restHeaders,
			dontThrowOnBadStatus,
			...restInit
		} = ((isNoBody ? bodyOrOptions : options) as ApiFetchOptions) ?? {};
		const { headers: authHeaders, ...authRest } = this.authInit;
		const isFormData = body instanceof FormData;

		const init: RequestInit = {
			...authRest,
			...restInit,
			method,
			headers: {
				...(isFormData ? {} : { 'Content-Type': 'application/json' }),
				...(authHeaders as Record<string, string>),
				...(restHeaders as Record<string, string>)
			}
		};
		if (body !== undefined) {
			init.body = isFormData ? body : JSON.stringify(body);
		}

		const res = await fetch(new URL(path, this.baseURL), init);
		if (!dontThrowOnBadStatus && !res.ok) {
			throw new Error(await res.text());
		}
		return res;
	}

	protected async apiFetch<T = void>(
		method: NoBodyMethod,
		path: string,
		options?: ApiFetchOptions
	): Promise<T>;
	protected async apiFetch<T = void>(
		method: BodyMethod,
		path: string,
		body?: unknown,
		options?: ApiFetchOptions
	): Promise<T>;
	protected async apiFetch<T = void>(
		method: HttpMethod,
		path: string,
		bodyOrOptions?: unknown,
		options?: ApiFetchOptions
	): Promise<T> {
		const res = await this._request(method, path, bodyOrOptions, options);
		if (res.status !== 204 && res.headers.get('content-type')?.includes('application/json')) {
			return res.json() as Promise<T>;
		}
		return undefined as T;
	}

	protected async apiStream(
		method: NoBodyMethod,
		path: string,
		options?: ApiFetchOptions
	): Promise<Response>;
	protected async apiStream(
		method: BodyMethod,
		path: string,
		body?: unknown,
		options?: ApiFetchOptions
	): Promise<Response>;
	protected async apiStream(
		method: HttpMethod,
		path: string,
		bodyOrOptions?: unknown,
		options?: ApiFetchOptions
	): Promise<Response> {
		return this._request(method, path, bodyOrOptions, options);
	}
}
